const crypto = require("crypto");
import React, { useMemo } from "react";
import cx from "classnames";
import { MDXProvider } from "@mdx-js/react";
import { extractTags } from "~/helpers/extract-tags";
import Blcokquote from "~/views/components/content/blockquote";
import ExternalLink from "~/views/components/content/external-link";
import InternalLink from "~/views/components/content/internal-link";
import AppLink from "~/views/components/content/app-link";
import Media from "~/views/components/content/media";
import Callout from "~/views/components/content/callout";
import Code from "~/views/components/content/code";
import Tweet from "~/views/components/content/tweet";
import InlineLink from "~/views/components/content/inline-link";
import ContributorCard from "~/views/components/content/contributor";
import TwitterCTA from "~/views/components/content/twitter-cta";
import NewsletterCTA from "~/views/components/content/newsletter-cta";
import Podcast from "~/views/components/content/podcast";
import Spotify from "~/views/components/content/spotify";
import Figma from "~/views/components/content/figma";
import FAQ from "~/views/components/content/faq";
import Resource from "~/views/components/content/resource";
import useImageModal from "~/helpers/use-image-modal";
import Quiz from "~/views/components/content/quiz";
import Note from "~/views/components/content/note";
import Book from "~/views/components/content/book";
import InlineTag from "~/views/components/content/tag";
import InlineAffiliateLink from "~/views/components/content/affiliate-link";
import Summary from "~/views/components/content/summary";
import Flow from "~/views/components/content/flow";
import AffiliateBanner from "~/views/components/content/affiliate-banner";
import ImageComparison from "~/views/components/content/image-comparison";
import Paragraph from "~/views/components/content/paragraph";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import Asset from "~/models/asset";

type Props = {
  id?: string;
  className?: string;
  content: string;
  references?: any[];
};

export const renderNode = (
  node: any,
  index: number = 0,
  components: any
): React.ReactNode => {
  if (!node) return null;

  // Handle text nodes
  if (node.type === "text") {
    return node.value;
  }

  // Handle markdown AST nodes
  if (node.type === "heading") {
    const props = { key: index };
    const children =
      node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex, components)
      ) || [];

    // Map heading depth to appropriate component
    if (node.depth === 2) {
      return React.createElement(components.h2, props, ...children);
    } else if (node.depth === 3) {
      return React.createElement(components.h3, props, ...children);
    } else if (node.depth === 4) {
      return React.createElement(components.h4, props, ...children);
    } else if (node.depth === 5) {
      return React.createElement(components.h5, props, ...children);
    } else {
      // Fallback for other heading levels
      return React.createElement(`h${node.depth}`, props, ...children);
    }
  }

  if (node.type === "paragraph") {
    const props = { key: index };
    const children =
      node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex, components)
      ) || [];
    return React.createElement(components.p, props, ...children);
  }

  if (node.type === "blockquote") {
    const props = { key: index };
    const children =
      node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex, components)
      ) || [];
    return React.createElement(components.blockquote, props, ...children);
  }

  if (node.type === "list") {
    const props = { key: index };
    const children =
      node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex, components)
      ) || [];

    if (node.ordered) {
      return React.createElement(components.ol, props, ...children);
    } else {
      return React.createElement(components.ul, props, ...children);
    }
  }

  if (node.type === "listItem") {
    const props = { key: index };
    const children =
      node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex, components)
      ) || [];
    return React.createElement(components.li, props, ...children);
  }

  if (node.type === "strong") {
    const props = { key: index };
    const children =
      node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex, components)
      ) || [];
    return React.createElement(components.strong, props, ...children);
  }

  if (node.type === "emphasis") {
    const props = { key: index };
    const children =
      node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex, components)
      ) || [];
    return React.createElement(components.em, props, ...children);
  }

  if (node.type === "link") {
    const props = { key: index, href: node.url, title: node.title };
    const children =
      node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex, components)
      ) || [];
    return React.createElement(components.a, props, ...children);
  }

  if (node.type === "table") {
    const props = { key: index };
    const children =
      node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex, components)
      ) || [];
    return React.createElement(components.table, props, ...children);
  }

  if (node.type === "tableRow") {
    const props = { key: index };
    const children =
      node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex, components)
      ) || [];
    return React.createElement(components.tableRow, props, ...children);
  }

  if (node.type === "tableCell") {
    const props = { key: index };
    const children =
      node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex, components)
      ) || [];
    return React.createElement(components.tableCell, props, ...children);
  }

  // Handle raw HTML nodes that contain embedded components
  if (node.type === "html") {
    const htmlContent = node.value || "";

    // Check for EmbeddedAsset
    const assetMatch = htmlContent.match(
      /<EmbeddedAsset\s+id="([^"]+)"(?:\s+type="([^"]+)")?[^>]*\s*\/>/
    );
    if (assetMatch) {
      const [, id, type = "asset"] = assetMatch;
      return React.createElement(components.EmbeddedAsset, {
        id,
        type,
        key: index,
      });
    }

    // Check for EmbeddedEntry
    const entryMatch = htmlContent.match(
      /<EmbeddedEntry\s+id="([^"]+)"(?:\s+type="([^"]+)")?[^>]*\s*\/>/
    );
    if (entryMatch) {
      const [, id, type = "entry"] = entryMatch;
      return React.createElement(components.EmbeddedEntry, {
        id,
        type,
        key: index,
      });
    }

    // Check for InlineLink
    const inlineLinkMatch = htmlContent.match(
      /<InlineLink\s+id="([^"]+)"(?:\s+type="([^"]+)")?[^>]*\s*\/>/
    );
    if (inlineLinkMatch) {
      const [, id, type = "inline-link"] = inlineLinkMatch;
      return React.createElement(components.InlineLink, {
        id,
        type,
        key: index,
      });
    }

    // Check if it contains any embedded components we might have missed
    if (htmlContent.includes("Embedded")) {
      // console.log("Unmatched embedded content:", htmlContent);
    }

    // If it's other HTML content, render it as raw HTML

    return (
      <div key={index} dangerouslySetInnerHTML={{ __html: htmlContent }} />
    );
  }

  // Handle root and other container nodes
  if (node.type === "root" || node.type === "html" || node.type === "body") {
    return (
      node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex, components)
      ) || null
    );
  }

  // Handle HTML element nodes (from our manual transformation)
  if (node.type === "htmlElement") {
    const props = { key: index };
    const children =
      node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex, components)
      ) || [];

    // Check if we have a custom component for this tag
    const Component = components[node.tagName];
    if (Component) {
      return React.createElement(Component, props, ...children);
    }

    // Fallback to regular HTML element
    return React.createElement(node.tagName, props, ...children);
  }

  // Handle HTML element nodes (from rehype processing)
  if (node.type === "element") {
    const props = { key: index };
    const children =
      node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex, components)
      ) || [];

    // Check if we have a custom component for this tag
    const Component = components[node.tagName];
    if (Component) {
      return React.createElement(Component, props, ...children);
    }

    // Fallback to regular HTML element
    return React.createElement(node.tagName, props, ...children);
  }

  // Handle doctype and other non-renderable nodes
  if (node.type === "doctype" || node.type === "comment") {
    return null;
  }

  // Fallback for unknown node types
  // console.warn("Unknown node type:", node.type, node);
  return null;
};

const transformHtmlNodesInMarkdown = (children: any[]): any[] => {
  const result = [];
  let i = 0;

  while (i < children.length) {
    const node = children[i];

    // Check if this is an opening HTML tag in markdown AST
    if (node.type === "html" && node.value.match(/^<(\w+)>$/)) {
      const tagMatch = node.value.match(/^<(\w+)>$/);
      const tagName = tagMatch[1];
      const closingTag = `</${tagName}>`;

      // Find the matching closing tag
      let closingIndex = -1;
      for (let j = i + 1; j < children.length; j++) {
        if (children[j].type === "html" && children[j].value === closingTag) {
          closingIndex = j;
          break;
        }
      }

      if (closingIndex !== -1) {
        // Collect all nodes between opening and closing tags
        const elementChildren = [];
        for (let k = i + 1; k < closingIndex; k++) {
          elementChildren.push(children[k]);
        }

        // Create new element node (markdown-style but with HTML structure)
        const elementNode = {
          type: "htmlElement",
          tagName: tagName,
          properties: {},
          children: transformHtmlNodesInMarkdown(elementChildren), // Recursively transform children
          position: {
            start: node.position?.start,
            end: children[closingIndex].position?.end,
          },
        };

        result.push(elementNode);
        i = closingIndex + 1; // Skip past the closing tag
      } else {
        // No matching closing tag found, treat as regular html node
        result.push(node);
        i++;
      }
    } else {
      // Regular node, just add it
      result.push(node);
      i++;
    }
  }

  return result;
};

const MarkdownRenderer = ({
  id,
  className,
  content,
  references = [],
}: Props) => {
  const file = unified().use(remarkParse).use(remarkGfm).parse(content);

  // Apply our manual HTML transformation to the markdown AST
  const walkAndTransform = (node: any): any => {
    if (node.children) {
      node.children = transformHtmlNodesInMarkdown(node.children);
      node.children.forEach(walkAndTransform);
    }
    return node;
  };

  const transformedFile = walkAndTransform(file);

  // Helper function to find a reference by ID
  const findReference = (id: string) => {
    return references.find(
      (ref) => ref.node?.yamlId === id || ref.node?.frontmatter?.id === id
    );
  };

  // Custom components that match the RichText styling
  const components = {
    h2: ({ children, ...props }: any) => {
      const text = React.Children.toArray(children).join("");
      const id = crypto.createHash("md5").update(text).digest("hex");
      return (
        <div id={id} className="heading mt-16 sm:mt-[128px] mb-6 sm:mb-[34px]">
          <h2 className="font-bold text-xl sm:text-5xl mb-2">{children}</h2>
          <div className="h-[2px] bg-neutral-98 relative">
            <div className="absolute left-0 h-[2px] w-[20%] bg-cyan"></div>
          </div>
        </div>
      );
    },

    h3: ({ children, ...props }: any) => {
      const text = React.Children.toArray(children).join("");
      const id = crypto.createHash("md5").update(text).digest("hex");
      return (
        <h3
          id={id}
          className="heading mt-12 sm:mt-[90px] mb-[34px] font-bold text-xl sm:text-3xl leading-[1.6] bg-neutral-99 py-1 px-3 border-l-4 border-cyan"
          {...props}>
          {children}
        </h3>
      );
    },

    h4: ({ children, ...props }: any) => (
      <h4
        className="border-l-4 border-cyan pl-3 lg:pl-2 mb-[34px] text-[18px] font-bold leading-[2.0]"
        {...props}>
        {children}
      </h4>
    ),

    h5: ({ children, ...props }: any) => (
      <h5 className="mb-[34px] font-bold text-[17px] leading-[2.0]" {...props}>
        {children}
      </h5>
    ),

    // Paragraphs
    p: ({ children, ...props }: any) => <Paragraph>{children}</Paragraph>,

    u: ({ children, ...props }: any) => (
      <u className="text-underline font-bold">{children}</u>
    ),

    // Lists
    ul: ({ children, ...props }: any) => (
      <ul
        className="mb-12 sm:mb-16 px-5 lg:px-10 bg-neutral-99 rounded sm:rounded-lg marker:text-cyan list-outside list-disc"
        {...props}>
        {children}
      </ul>
    ),

    ol: ({ children, ...props }: any) => (
      <ol
        className="mb-12 sm:mb-16 px-5 lg:px-10 bg-neutral-99 rounded-lg list-decimal list-outside marker:text-cyan"
        {...props}>
        {children}
      </ol>
    ),

    li: ({ children, ...props }: any) => (
      <li
        className="richtext-list-item ml-4 py-3 sm:py-4 font-medium text-xs sm:text-sm leading-[1.6] text-neutral-30"
        {...props}>
        {children}
      </li>
    ),

    // Blockquotes
    blockquote: ({ children, ...props }: any) => (
      <div className="richtext-quote flex gap-2 mb-[34px] sm:gap-6 items-start p-4 sm:p-8 bg-neutral-99 rounded-lg text-neutral-30 font-regular">
        <img
          src="/images/quote.svg"
          width="24"
          height="24"
          alt="Quote"
          loading="lazy"
        />
        <div>
          <div className="font-medium leading-8 break-all" {...props}>
            {children}
          </div>
        </div>
      </div>
    ),

    // Tables
    table: ({ children, ...props }: any) => (
      <div className="pb-1 [overflow:overlay] mb-12 sm:mb-16">
        <table
          className="richtext-table border-2 border-neutral-90 shadow-neutral rounded-lg border-spacing-0 border-separate overflow-hidden w-full"
          {...props}>
          <tbody>{children}</tbody>
        </table>
      </div>
    ),

    tableRow: ({ children, ...props }: any) => {
      // Add index to each cell to identify first column
      const enhancedChildren = React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<any>, {
            "data-cell-index": index,
          });
        }
        return child;
      });

      return (
        <tr className="border-neutral-90 dashed-b last:bg-none" {...props}>
          {enhancedChildren}
        </tr>
      );
    },

    tableCell: ({ children, ...props }: any) => {
      return (
        <td
          className="break-keep py-5 px-4 border-r-2 border-neutral-90 last:border-r-0 text-xs sm:text-sm font-medium"
          {...props}>
          {children}
        </td>
      );
    },

    th: ({ children, ...props }: any) => (
      <th
        className="break-keep bg-neutral-99 py-5 px-4 border-r-2 border-r-neutral-90 last:border-r-0 dashed-b text-xs sm:text-sm font-bold"
        {...props}>
        {children}
      </th>
    ),

    // Text formatting
    strong: ({ children, ...props }: any) => (
      <b className="font-bold" {...props}>
        {children}
      </b>
    ),

    b: ({ children, ...props }: any) => (
      <b className="font-bold" {...props}>
        {children}
      </b>
    ),

    em: ({ children, ...props }: any) => (
      <b className="font-bold" {...props}>
        {children}
      </b>
    ),

    // Links
    a: ({ children, href, ...props }: any) => (
      <InlineLink to={href} {...props}>
        {children}
      </InlineLink>
    ),

    // Custom MDX components
    EmbeddedAsset: ({ id, type, ...props }: any) => {
      const { handleActivation } = useImageModal();
      const ref = findReference(id);

      if (!ref?.node) {
        return (
          <div className="my-8 sm:my-16 cursor-zoom-in">
            <div className="bg-neutral-99 pt-4 sm:pt-6 pb-4 px-4 sm:px-6 rounded sm:rounded-lg">
              <div className="block rounded-lg isolate bg-neutral-99 p-8 text-center text-neutral-60">
                Embedded Asset: {id} (Type: {type}) - Not Found
              </div>
            </div>
          </div>
        );
      }

      const asset = new Asset(ref.node);

      const { url, title, description, contentType } = asset;

      const { tags, content } = extractTags(description || "");

      if (contentType && contentType.indexOf("image") !== -1) {
        return (
          <div
            className={cx("my-8 sm:my-16 cursor-zoom-in", {
              "mb-6 sm:mb-[34px]": tags.indexOf("max-height=356") !== -1,
            })}
            onClick={() =>
              handleActivation(asset.imgix?.url({ w: 1500 }), tags)
            }>
            <div
              className={cx({
                "bg-neutral-99 pt-4 sm:pt-6 pb-4 px-4 sm:px-6 rounded sm:rounded-lg":
                  tags.indexOf("background-gray") !== -1,
              })}>
              {asset.imgix?.url && (
                <img
                  className={cx(
                    `block rounded-lg isolate bg-neutral-99 max-w-full`,
                    {
                      "border-2 border-cyan shadow-cyan":
                        tags.indexOf("border") !== -1,
                      "w-[260px] mx-auto border border-neutral-90-0.6":
                        tags.indexOf("screenshot") !== -1 &&
                        tags.indexOf("mobile") !== -1,
                      "border border-neutral-90-0.6":
                        tags.indexOf("border-gray") !== -1,
                      "!max-h-[356px] !mx-auto":
                        tags.indexOf("max-height=356") !== -1,
                      "!max-w-[480px] !mx-auto":
                        tags.indexOf("max-width=480") !== -1,
                    }
                  )}
                  src={asset.imgix.url({ w: 1500 })}
                  alt={title || ""}
                />
              )}
            </div>
            {content && (
              <p
                className={cx("mt-2 text-xs text-neutral-60 break-all", {
                  "text-center": tags.indexOf("screenshot") !== -1,
                })}>
                {content}
              </p>
            )}
          </div>
        );
      }

      if (contentType && contentType.indexOf("video") !== -1) {
        return (
          <div
            className={cx("my-8 sm:my-16", {
              "mb-6 sm:mb-[34px]": tags.indexOf("max-height=356") !== -1,
            })}>
            <video
              autoPlay
              muted
              loop
              playsInline
              className={cx("rounded-lg isolate", {
                "border-2 border-cyan shadow-cyan":
                  tags.indexOf("border") !== -1,
                "border border-neutral-90-0.6":
                  tags.indexOf("border-gray") !== -1,
                "w-[260px] mx-auto border border-neutral-90-0.6":
                  tags.indexOf("screenshot") !== -1 &&
                  tags.indexOf("mobile") !== -1,
                "!max-h-[356px] !mx-auto":
                  tags.indexOf("max-height=356") !== -1,
              })}>
              <source src={ref.node.imgix?.url} type={contentType} />
            </video>
            {content && (
              <p
                className={cx("mt-2 text-xs text-neutral-60 break-all", {
                  "text-center": tags.indexOf("screenshot") !== -1,
                })}>
                {content}
              </p>
            )}
          </div>
        );
      }

      // Fallback for other asset types
      return (
        <div className="my-8 sm:my-16 cursor-zoom-in">
          <div className="bg-neutral-99 pt-4 sm:pt-6 pb-4 px-4 sm:px-6 rounded sm:rounded-lg">
            <div className="block rounded-lg isolate bg-neutral-99 p-8 text-center text-neutral-60">
              Asset: {title || id} (Type: {contentType || type})
            </div>
          </div>
        </div>
      );
    },

    EmbeddedEntry: ({ id, type, ...props }: any) => {
      const ref = findReference(id);

      if (!ref?.node) {
        return (
          <div className="my-8 sm:my-16 p-4 bg-neutral-99 rounded-lg text-center text-neutral-60">
            Embedded Entry: {id} (Type: {type}) - Not Found
          </div>
        );
      }

      const entry = ref.node;
      const entryType = entry.internal?.type || entry.__typename;

      // Create a node structure that matches the richtext implementation
      const nodeData = entry;

      // Handle inline components first
      // if (entryType === "ComponentInlineAffiliateLinkYaml") {
      //   return <InlineAffiliateLink node={nodeData} />;
      // }

      // if (entryType === "ComponentInlineTagYaml") {
      //   return <InlineTag node={nodeData} />;
      // }

      // Handle block components
      if (entryType === "ComponentImageComparisonYaml") {
        return <ImageComparison node={nodeData} />;
      }

      if (entryType === "ComponentAffiliateBannerYaml") {
        return <AffiliateBanner node={nodeData} />;
      }

      if (entryType === "ComponentFlowYaml") {
        return <Flow node={nodeData} />;
      }

      if (entryType === "ComponentBookYaml") {
        return <Book node={nodeData} />;
      }

      if (entryType === "ComponentQuizMdx") {
        return <Quiz node={nodeData} />;
      }

      if (entryType === "ComponentBlockquoteYaml") {
        return <Blcokquote node={nodeData} />;
      }

      if (entryType === "ComponentLinkYaml") {
        if (entry.url && entry.url.indexOf("www.youtube.com") !== -1) {
          return <Media node={nodeData} />;
        }
        return <ExternalLink node={nodeData} />;
      }

      if (entryType === "ComponentAppYaml") {
        return <AppLink node={nodeData} />;
      }

      if (entryType === "PageMdx") {
        return <InternalLink node={nodeData} />;
      }

      if (entryType === "ComponentCalloutYaml") {
        return <Callout node={nodeData} />;
      }

      if (entryType === "ComponentCodeYaml") {
        return <Code node={nodeData} />;
      }

      if (entryType === "ComponentNoteMdx") {
        return <Note node={nodeData} />;
      }

      if (entryType === "ComponentEmbedYaml") {
        if (entry.type === "Tweet") {
          return <Tweet node={nodeData} />;
        }

        if (entry.type === "Podcast") {
          return <Podcast node={nodeData} />;
        }

        if (entry.type === "Spotify") {
          return <Spotify node={nodeData} />;
        }

        if (entry.type === "Figma") {
          return <Figma node={nodeData} />;
        }

        return (
          <div className="my-8 sm:my-16 mx-auto flex flex-row justify-center">
            <div
              className="overflow-hidden isolate w-full"
              dangerouslySetInnerHTML={{
                __html: entry.code?.code || entry.code || "",
              }}
            />
          </div>
        );
      }

      if (entryType === "ComponentContributorYaml") {
        return <ContributorCard node={nodeData} />;
      }

      if (entryType === "ComponentNewsletterCtaYaml") {
        return <NewsletterCTA node={nodeData} />;
      }

      if (entryType === "ComponentTwitterCtaYaml") {
        return <TwitterCTA node={nodeData} />;
      }

      if (entryType === "ComponentFaqMdx") {
        return <FAQ node={nodeData} />;
      }

      if (entryType === "ComponentResourceYaml") {
        return <Resource node={nodeData} />;
      }

      if (entryType === "ComponentSummaryMdx") {
        return <Summary node={nodeData} />;
      }

      // Fallback for unknown entry types
      return (
        <div className="my-8 sm:my-16 p-4 bg-neutral-99 rounded-lg text-center text-neutral-60">
          Embedded Entry: {entry.title || entry.name || id} (Type:{" "}
          {entryType || type})
        </div>
      );
    },

    InlineLink: ({ id, type, ...props }: any) => {
      const ref = findReference(id);

      if (!ref?.node) {
        return (
          <div className="my-8 sm:my-16 p-4 bg-neutral-99 rounded-lg text-center text-neutral-60">
            Inline Link: {id} (Type: {type}) - Not Found
          </div>
        );
      }

      const entry = ref.node;
      const entryType = entry.internal?.type || entry.__typename;

      // Create a node structure that matches the richtext implementation
      const nodeData = entry;

      // Handle inline components first
      if (entryType === "ComponentInlineAffiliateLinkYaml") {
        return <InlineAffiliateLink node={nodeData} />;
      }

      if (entryType === "ComponentInlineTagYaml") {
        return <InlineTag node={nodeData} />;
      }

      return null;
    },

    // Other content components
    Media,
    Tweet,
    Podcast,
    Spotify,
    Figma,
    Note,
    Summary,
    ExternalLink,
    InternalLink,
  };

  // Render the parsed content
  const renderedContent = useMemo(() => {
    return renderNode(transformedFile, 0, components);
  }, [transformedFile]);

  // For pre-processed content
  return (
    <div
      id={id}
      className={cx("richtext font-feature-normal tracking-[0em]", className)}>
      <MDXProvider components={components}>{renderedContent}</MDXProvider>
    </div>
  );
};

export default MarkdownRenderer;
